import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { useDataset } from "../../hooks/useDataset.ts";
import { useMutate } from "../../hooks/useMutate.ts";
import {
  addRecurringCost,
  addRecurringCostWithPost,
  placeRecurringUnderPost,
  endRecurringCost,
  restoreRecurringCost,
  setRecurringCostEndedFrom,
  updateRecurringCost,
} from "../../../store/actions.ts";
import { parseMoneyInput } from "../../moneyInput.ts";
import { currentMonth } from "../../../store/index.ts";
import { Section } from "../../components/Section.tsx";
import { RecurringSplitEditor, type RecurringSplitSelection } from "../../components/RecurringSplitEditor.tsx";
import { monthOf } from "../../../domain/months.ts";
import { toDayOrdinal } from "../../../domain/days.ts";
import { startDateNeedsDay } from "../../../domain/granularity.ts";
import type { IsoDate, Recurrence, RecurringCost } from "../../../domain/types.ts";

/** Also used by `PurchaseDialog`'s "+ New recurring cost…" control. */
export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "YYYY-MM" is 7 characters; "YYYY-MM-DD" is 10. Same idiom PurchaseDialog uses. */
export function isDayGranular(date: IsoDate): boolean {
  return date.length > 7;
}

/**
 * True only for a string this cost's recurrence kind could actually walk:
 * `everyNMonths` tolerates either granularity (the walk normalises through
 * `monthOf`), the other two require a day-granular date because they reach
 * `addDays` directly. Reuses the domain's own parsers rather than a second,
 * possibly-diverging regex.
 *
 * A day-SHAPED value is routed through `toDayOrdinal` even under
 * `everyNMonths`: `monthOf` only extracts the month and never checks the day,
 * so without this a calendar-impossible date like "2026-09-31" (September has
 * 30 days) would pass here and could still be stored, only to throw three
 * months later out of the fold.
 *
 * Exported for `RecurringSection.test.ts` — this is the one thing standing
 * between the "Starts" field and C2 (an empty or malformed value bricking
 * every route the fold touches), so it is tested directly rather than only
 * through a DOM interaction.
 */
export function isValidStartDate(
  value: string,
  kind: Recurrence["kind"],
  anchoring: RecurringCost["anchoring"],
): boolean {
  // The same predicate the store's validation asks, so the field refuses
  // exactly what the write would refuse rather than issuing a doomed mutate.
  if (startDateNeedsDay(kind, anchoring) && !isDayGranular(value)) return false;
  try {
    if (isDayGranular(value)) toDayOrdinal(value);
    else monthOf(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates the "Ends" column. Unlike `startDate`, `endedFrom` carries NO
 * granularity rule (§10 of the design) — it is only ever compared
 * lexicographically via `isBefore` in `domain/occurrences.ts` and never
 * reaches `addDays`, so a month-only cancellation is exactly as valid as a
 * day-granular one regardless of the cost's recurrence kind. It still has to
 * be a real calendar date: "2026-02-30" is refused the same way an
 * impossible `startDate` already is.
 *
 * An empty string is valid — it means "clear the cancellation" — and is what
 * lets the field double as the un-end control.
 *
 * Exported for `RecurringSection.test.ts`.
 */
export function isValidEndedFrom(value: string): boolean {
  if (value === "") return true;
  try {
    if (isDayGranular(value)) toDayOrdinal(value);
    else monthOf(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * What the "Starts" value becomes when the owner switches to a day-granular
 * unit, or to `lastCharge` anchoring: a month-only value expands to the first
 * of that month, in the SAME mutate call that changes the kind or anchoring —
 * the walk must never see the old pairing even for one render. Which pairings
 * need a day is `startDateNeedsDay`, the predicate the store asks; `anchoring`
 * is required so a caller cannot silently answer "calendar" for an anchored
 * cost. An already day-granular value passes through untouched.
 *
 * Exported for `RecurringSection.test.ts`.
 */
export function normalizedStartDateFor(
  kind: Recurrence["kind"],
  startDate: IsoDate,
  anchoring: RecurringCost["anchoring"],
): IsoDate {
  if (!startDateNeedsDay(kind, anchoring) || isDayGranular(startDate)) return startDate;
  return `${startDate}-01`;
}

function dateForInput(value: IsoDate | undefined, precision: "month" | "date"): string {
  if (!value) return "";
  return precision === "date" ? (isDayGranular(value) ? value : `${value}-01`) : value.slice(0, 7);
}

export function RecurringSection() {
  const dataset = useDataset();
  const { mutate, error } = useMutate();
  const [newName, setNewName] = useState("");
  const [newAmount, setNewAmount] = useState(`0 ${dataset.settings.baseCurrency}`);
  const [destinationKind, setDestinationKind] = useState<"existing" | "dedicated" | "unassigned">("dedicated");
  const [newPostName, setNewPostName] = useState("");
  const [newInterval, setNewInterval] = useState("1");
  const [newUnit, setNewUnit] = useState<Recurrence["kind"]>("everyNMonths");
  const [newWeekday, setNewWeekday] = useState(1);
  const [newStartDate, setNewStartDate] = useState<IsoDate>(currentMonth);
  const [newDatePrecision, setNewDatePrecision] = useState<"month" | "date">("month");
  const [newAnchoring, setNewAnchoring] = useState<RecurringCost["anchoring"]>("calendar");
  const [folderId, setFolderId] = useState<string | null>(null);
  const chargedIds = new Set(dataset.purchases.flatMap(p => p.source ? [p.source.recurringId] : []));
  const [newSelection, setNewSelection] = useState<RecurringSplitSelection>({ splitMode: "percent", splits: [{ postId: "", value: 100, absorbsRemainder: true }] });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [endDatePrecision, setEndDatePrecision] = useState<Record<string, "month" | "date">>({});
  const [editSelection, setEditSelection] = useState<RecurringSplitSelection | null>(null);
  const ordered = [...dataset.recurring].sort((a, b) => a.order - b.order);
  const base = dataset.settings.baseCurrency;
  const activePosts = dataset.posts.filter(post => !post.archived).sort((a, b) => a.order - b.order);
  const parsedNewAmount = parseMoneyInput(newAmount, dataset.currencies, base);
  const validNewSplits = newSelection.splits.length > 0 && newSelection.splits.every(split => activePosts.some(post => post.id === split.postId));
  const validNewInterval = Number.isInteger(Number(newInterval)) && Number(newInterval) >= 1;
  const validNewStart = isValidStartDate(newStartDate, newUnit, newAnchoring);

  function amountCell(cost: RecurringCost) {
    return (
      <Input
        className="h-8 w-28"
        // text, not number: a number input strips the "$" before
        // parseMoneyInput ever sees it, so "30$" would lose its currency.
        type="text"
        aria-label={`Amount for ${cost.name}`}
        defaultValue={`${cost.amount.amount} ${cost.amount.currency}`}
        onBlur={(event) => {
          // Captured before mutate: mutate defers behind the write queue and
          // React resets the DOM value first, so reading event.target.value
          // inside the callback would commit the previous keystroke.
          const typed = event.target.value;
          // (text, currency options, fallback) — the same three arguments
          // PurchaseDialog passes. The options list is what lets "30$" resolve
          // to USD; the fallback is used when no symbol or code is typed.
          const parsed = parseMoneyInput(typed, dataset.currencies, cost.amount.currency);
          if (!parsed) return;
          mutate((draft) => updateRecurringCost(draft, cost.id, { amount: parsed }));
        }}
      />
    );
  }

  function intervalCell(cost: RecurringCost) {
    return (
      <div className="flex items-center gap-1">
        <Input
          className="h-8 w-20"
          type="number"
          min={1}
          step={1}
          aria-label={`Interval for ${cost.name}`}
          value={cost.recurrence.n}
          onChange={(event) => {
            // Captured BEFORE mutate: mutate defers behind the write queue and
            // React resets the DOM value first, so reading it inside the
            // callback commits the previous keystroke.
            const n = Number(event.target.value);
            if (!Number.isInteger(n) || n < 1) return;
            mutate((draft) =>
              updateRecurringCost(draft, cost.id, { recurrence: { ...cost.recurrence, n } }),
            );
          }}
        />
        <NativeSelect
          className="h-8 w-auto text-xs md:text-xs"
          aria-label={`Unit for ${cost.name}`}
          value={cost.recurrence.kind}
          onChange={(event) => {
            const kind = event.target.value as Recurrence["kind"];
            const recurrence: Recurrence =
              kind === "everyNWeeks"
                ? { kind, n: cost.recurrence.n, weekday: 1 }
                : { kind, n: cost.recurrence.n };
            // Switching to a day-granular kind against a month-only
            // startDate is exactly how C1 bricked the app: the walk hands
            // "2026-09" to addDays and throws. Expand it to the first of
            // that month IN THIS SAME mutate call, so the pairing the store
            // now validates never has a chance to go wrong.
            const startDate = normalizedStartDateFor(kind, cost.startDate, cost.anchoring);
            mutate((draft) =>
              updateRecurringCost(draft, cost.id, { recurrence, startDate }),
            );
          }}
        >
          <option value="everyNDays">days</option>
          <option value="everyNWeeks">weeks</option>
          <option value="everyNMonths">months</option>
        </NativeSelect>
        {cost.recurrence.kind === "everyNWeeks" && (
          <NativeSelect
            className="h-8 w-auto text-xs md:text-xs"
            aria-label={`Weekday for ${cost.name}`}
            value={cost.recurrence.weekday}
            onChange={(event) => {
              const weekday = Number(event.target.value);
              mutate((draft) =>
                updateRecurringCost(draft, cost.id, {
                  recurrence: { kind: "everyNWeeks", n: cost.recurrence.n, weekday },
                }),
              );
            }}
          >
            {WEEKDAYS.map((day, index) => (
              <option key={day} value={index}>
                {day}
              </option>
            ))}
          </NativeSelect>
        )}
      </div>
    );
  }

  return (
    <Section
      title="Recurring costs"
      hint="Bills that repeat. Each one projects forward as EXPECTED spending and is confirmed from the month view when the money actually moves — expected never touches a post's real balance. Anchoring decides where the next charge is measured from: the calendar, or the date the last one actually charged. Use the second for a bill that can charge early, like a phone plan that recharges when its data runs out."
    >
      <div className="mb-4 space-y-1">
        <label htmlFor="allow-existing-recurring-cost" className="flex min-h-8 cursor-pointer items-center gap-2 text-sm">
          <input
            id="allow-existing-recurring-cost"
            type="checkbox"
            className="size-4 accent-primary"
            checked={dataset.settings.allowExistingRecurringCost === true}
            onChange={event => {
              const enabled = event.target.checked;
              void mutate(draft => { draft.settings.allowExistingRecurringCost = enabled; });
            }}
          />
          Allow choosing an existing recurring cost when adding a purchase
        </label>
        <p className="max-w-[75ch] text-xs leading-relaxed text-budget-ink-muted">
          By default, a purchase can create a new recurring cost. Enable this to record a charge for an existing bill from Add purchase. Existing purchase links remain editable.
        </p>
      </div>
      <p className="mb-3 text-xs"><a className="underline underline-offset-2" href="#budget-folders">Move and reorder bills in Budget folders</a></p>
      {error && <p role="alert" className="mb-3 text-sm text-destructive">Could not save: {error}</p>}
      {ordered.length === 0 ? (
        <p className="text-sm text-budget-ink-muted">
          No recurring costs yet. Add rent, a subscription, or a phone bill below.
        </p>
      ) : (
        <div className="-mr-2 overflow-x-auto pr-2">
          <table className="w-full min-w-[46rem] text-sm">
            <thead className="text-left">
              <tr className="border-b border-budget-rule text-[0.6875rem] uppercase tracking-wider text-budget-ink-muted">
                <th className="py-2 font-medium">Name</th>
                <th className="py-2 font-medium">Amount</th>
                <th className="py-2 font-medium">Every</th>
                <th className="py-2 font-medium">Measured from</th>
                <th className="py-2 font-medium">Starts</th>
                <th className="py-2 font-medium">Ends</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {ordered.map((cost) => (<Fragment key={cost.id}>
                <tr
                  data-bill-id={cost.id}
                  className={`border-b last:border-0 ${cost.archived ? "opacity-50" : ""}`}
                >
                  <td className="py-2">
                    <Input
                      className="h-8 w-40"
                      value={cost.name}
                      onChange={(event) => {
                        const name = event.target.value;
                        mutate((draft) => updateRecurringCost(draft, cost.id, { name }));
                      }}
                    />
                  </td>
                  <td className="py-2">{amountCell(cost)}</td>
                  <td className="py-2">{intervalCell(cost)}</td>
                  <td className="py-2">
                    {/* Locked once the cost has charges: the store refuses the
                        change (a calendar grid cannot be invented for charges
                        that settled no slot), so say so rather than fail. */}
                    <NativeSelect
                      className="h-8 w-auto text-xs md:text-xs"
                      aria-label={`Anchoring for ${cost.name}`}
                      value={cost.anchoring}
                      disabled={chargedIds.has(cost.id)}
                      title={
                        chargedIds.has(cost.id)
                          ? "This cost has charges recorded, so its anchoring cannot change. End it and create a new one."
                          : undefined
                      }
                      onChange={(event) => {
                        const anchoring = event.target.value as RecurringCost["anchoring"];
                        // Same mutate as the anchoring change: a month-only
                        // start must never meet lastCharge, even for a render.
                        const startDate = normalizedStartDateFor(cost.recurrence.kind, cost.startDate, anchoring);
                        mutate((draft) => updateRecurringCost(draft, cost.id, { anchoring, startDate }));
                      }}
                    >
                      <option value="calendar">the calendar</option>
                      <option value="lastCharge">the last charge</option>
                    </NativeSelect>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                    {!startDateNeedsDay(cost.recurrence.kind, cost.anchoring) && <NativeSelect className="h-8 w-auto text-xs" aria-label={`Date precision for ${cost.name}`} value={isDayGranular(cost.startDate) ? "date" : "month"} onChange={event => {
                      const precision = event.target.value;
                      const startDate = precision === "date" ? `${monthOf(cost.startDate)}-01` : monthOf(cost.startDate);
                      void mutate(draft => updateRecurringCost(draft, cost.id, { startDate }));
                    }}><option value="month">Month</option><option value="date">Date</option></NativeSelect>}
                    {/* Uncontrolled: committing only happens on blur, and only
                        when the text parses as an IsoDate of the granularity
                        this cost's recurrence kind requires. Keyed on the
                        stored value so an external change (kind flip
                        auto-expanding it, or a rejected edit) is reflected
                        rather than left showing stale typed text. */}
                    <Input
                      key={`${cost.id}-${cost.startDate}`}
                      className="h-8 w-32"
                      type={isDayGranular(cost.startDate) ? "date" : "month"}
                      aria-label={`Start date for ${cost.name}`}
                      defaultValue={cost.startDate}
                      onBlur={(event) => {
                        const typed = event.target.value;
                        if (!isValidStartDate(typed, cost.recurrence.kind, cost.anchoring)) {
                          event.target.value = cost.startDate;
                          return;
                        }
                        mutate((draft) =>
                          updateRecurringCost(draft, cost.id, { startDate: typed }),
                        );
                      }}
                    />
                    </div>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                    <NativeSelect className="h-8 w-auto text-xs" aria-label={`End date precision for ${cost.name}`} value={endDatePrecision[cost.id] ?? (cost.endedFrom && isDayGranular(cost.endedFrom) ? "date" : "month")} onChange={event => setEndDatePrecision(precision => ({ ...precision, [cost.id]: event.target.value as "month" | "date" }))}><option value="month">Month</option><option value="date">Date</option></NativeSelect>
                    {/* Same idiom as "Starts": uncontrolled, committed on
                        blur, keyed on the stored value so an external change
                        (the end/restart button, or a rejected edit) shows up
                        rather than leaving stale typed text. Empty means "not
                        ended" and is itself a valid value — clearing it here
                        does the same thing as the "restart" button. */}
                    <Input
                      key={`${cost.id}-ends-${cost.endedFrom ?? ""}-${endDatePrecision[cost.id] ?? "stored"}`}
                      className="h-8 w-32"
                      type={endDatePrecision[cost.id] ?? (cost.endedFrom && isDayGranular(cost.endedFrom) ? "date" : "month")}
                      placeholder="—"
                      aria-label={`Ends date for ${cost.name}`}
                      defaultValue={dateForInput(cost.endedFrom, endDatePrecision[cost.id] ?? (cost.endedFrom && isDayGranular(cost.endedFrom) ? "date" : "month"))}
                      onBlur={(event) => {
                        const typed = event.target.value.trim();
                        if (!isValidEndedFrom(typed)) {
                          event.target.value = cost.endedFrom ?? "";
                          return;
                        }
                        mutate((draft) =>
                          setRecurringCostEndedFrom(
                            draft,
                            cost.id,
                            typed === "" ? undefined : typed,
                          ),
                        );
                      }}
                    />
                    </div>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button id={`bill-${cost.id}`} size="xs" variant="ghost" onClick={() => {
                        setEditingId(cost.id);
                        setEditSelection({ splitMode: cost.splitMode, splits: cost.splits.map(split => ({ ...split })) });
                      }}>Edit</Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        className="-mr-2"
                        onClick={() =>
                          mutate((draft) =>
                            cost.archived
                              ? restoreRecurringCost(draft, cost.id)
                              : endRecurringCost(draft, cost.id, currentMonth),
                          )
                        }
                      >
                        {cost.archived ? "restart" : "end"}
                      </Button>
                    </div>
                  </td>
                </tr>
                {editingId === cost.id && editSelection && (
                  <tr key={`${cost.id}-editor`}><td colSpan={7} className="py-3">
                    <div className="space-y-3 rounded-md border border-budget-rule p-3">
                      <RecurringSplitEditor {...editSelection} posts={activePosts} onChange={setEditSelection} />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={!editSelection.splits.every(split => activePosts.some(post => post.id === split.postId))} onClick={() => {
                          const selected = { splitMode: editSelection.splitMode, splits: editSelection.splits.map(split => ({ ...split })) };
                          void mutate(draft => updateRecurringCost(draft, cost.id, selected)).then(saved => { if (saved) setEditingId(null); });
                        }}>Save destinations</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </div>
                  </td></tr>
                )}
              </Fragment>))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 max-w-[70ch] text-xs leading-relaxed text-budget-ink-muted">
        Ending a cost — from the "end" button, or by typing a date into
        "Ends" directly — stops it from that date onward and leaves every past
        occurrence exactly as it was; clearing the date restarts it. Changing
        the amount only moves occurrences that have not been confirmed yet —
        what you already paid is recorded on the purchases themselves, so a
        price rise never rewrites history.
      </p>

      <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-budget-rule pt-5">
        <Input
          className="w-48"
          placeholder="New cost name"
          value={newName}
          onInput={(event) => setNewName(event.currentTarget.value)}
        />
        <Input className="w-32" type="text" aria-label="New bill amount" value={newAmount} onInput={event => setNewAmount(event.currentTarget.value)} />
        <label className="text-xs">Every
          <span className="mt-1 flex items-center gap-1">
            <Input className="w-20" type="number" min={1} step={1} aria-label="New bill interval" value={newInterval} onInput={event => setNewInterval(event.currentTarget.value)} />
            <NativeSelect aria-label="New bill recurrence unit" value={newUnit} onChange={event => {
              const kind = event.target.value as Recurrence["kind"];
              setNewUnit(kind);
              setNewStartDate(date => normalizedStartDateFor(kind, date, newAnchoring));
              if (startDateNeedsDay(kind, newAnchoring)) setNewDatePrecision("date");
            }}>
              <option value="everyNMonths">months</option>
              <option value="everyNWeeks">weeks</option>
              <option value="everyNDays">days</option>
            </NativeSelect>
          </span>
        </label>
        {newUnit === "everyNWeeks" && <NativeSelect aria-label="New bill weekday" value={newWeekday} onChange={event => setNewWeekday(Number(event.target.value))}>
          {WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}
        </NativeSelect>}
        <label className="text-xs">First due
          <span className="mt-1 flex items-center gap-1">
            {!startDateNeedsDay(newUnit, newAnchoring) && <NativeSelect aria-label="New bill date precision" value={newDatePrecision} onChange={event => {
              const precision = event.target.value as "month" | "date";
              setNewDatePrecision(precision);
              setNewStartDate(date => {
                const valid = isValidStartDate(date, "everyNMonths", "calendar") ? date : currentMonth;
                return precision === "date" ? normalizedStartDateFor("everyNDays", valid, "calendar") : monthOf(valid);
              });
            }}><option value="month">Month</option><option value="date">Date</option></NativeSelect>}
            <Input className="w-36" type={!startDateNeedsDay(newUnit, newAnchoring) && newDatePrecision === "month" ? "month" : "date"} aria-label="New bill start date" value={newStartDate} onChange={event => setNewStartDate(event.target.value)} aria-invalid={!validNewStart} />
          </span>
        </label>
        <NativeSelect aria-label="New bill anchoring" value={newAnchoring} onChange={event => {
          const anchoring = event.target.value as RecurringCost["anchoring"];
          setNewAnchoring(anchoring);
          setNewStartDate(date => normalizedStartDateFor(newUnit, date, anchoring));
          if (startDateNeedsDay(newUnit, anchoring)) setNewDatePrecision("date");
        }}>
          <option value="calendar">From calendar</option>
          <option value="lastCharge">From last charge</option>
        </NativeSelect>
        <NativeSelect aria-label="Bill folder" value={folderId ?? ""} onChange={event => setFolderId(event.target.value || null)}>
          <option value="">Unfiled</option>
          {dataset.folders.map(folder => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
        </NativeSelect>
        <NativeSelect aria-label="Destination type" value={destinationKind} onChange={event => setDestinationKind(event.target.value as "existing" | "dedicated" | "unassigned")}>
          <option value="dedicated">Create dedicated post</option>
          <option value="existing">Use existing post</option>
          <option value="unassigned">No post (unassigned)</option>
        </NativeSelect>
        {destinationKind === "dedicated" ? (
          <Input aria-label="Dedicated post name" placeholder={newName.trim() || "Same name as bill"} value={newPostName} onChange={event => setNewPostName(event.target.value)} />
        ) : destinationKind === "existing" ? (
          <RecurringSplitEditor {...newSelection} posts={activePosts} onChange={setNewSelection} />
        ) : null}
        <Button
          variant="outline"
          disabled={newName.trim() === "" || !parsedNewAmount || !validNewInterval || !validNewStart || (destinationKind === "existing" && !validNewSplits)}
          onClick={() => {
            const name = newName.trim();
            const amount = parsedNewAmount;
            if (!amount) return;
            const selectedFolder = folderId;
            const selectedKind = destinationKind;
            const dedicatedName = newPostName.trim() || name;
            const startDate = newStartDate;
            const n = Number(newInterval);
            const recurrence: Recurrence = newUnit === "everyNWeeks" ? { kind: newUnit, n, weekday: newWeekday } : { kind: newUnit, n };
            const anchoring = newAnchoring;
            const selectedSplits = newSelection.splits.map(split => ({ ...split }));
            const selectedMode = newSelection.splitMode;
            void mutate((draft) => {
              const input = {
                name,
                folderId: selectedFolder,
                archived: false,
                amount,
                startDate,
                recurrence,
                anchoring,
              };
              if (selectedKind === "dedicated") addRecurringCostWithPost(draft, input, dedicatedName);
              else {
                const splits = selectedKind === "unassigned" ? [] : selectedSplits;
                const bill = addRecurringCost(draft, { ...input, splitMode: selectedMode, splits });
                if (selectedFolder === null && splits.length === 1) placeRecurringUnderPost(draft, bill.id, splits[0]!.postId);
              }
            },
            ).then((saved) => {
              if (saved) { setNewName(""); setNewPostName(""); setNewAmount(`0 ${base}`); setNewSelection({ splitMode: "percent", splits: [{ postId: "", value: 100, absorbsRemainder: true }] }); }
            });
          }}
        >
          Add recurring cost
        </Button>
      </div>
      <p className="mt-2 text-xs text-budget-ink-muted">A dedicated post receives an automatic monthly allocation based on this bill’s schedule. If its first payment comes before enough has accumulated, its balance may be negative until later allocations catch up.</p>
    </Section>
  );
}
